import React, { FC, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import cns from 'classnames';
import {
  getAllSections,
  changeParam,
  changeOrder,
  ParamsDto,
  DataDto,
  changeBooleanParam,
  resetColorToDefault,
  internalizationState,
  toggleNavigationVisibility
} from '../../../stores/reducers/sections';
import Input from './Input/Input';
import Section from './Section/Section';
import Checkbox from './Checkbox/Checkbox';
import Radio from './Radio/Radio';
import MoreDetailsButton from './MoreDetailsButton/MoreDetailsButton';

interface Props {
  className?: string;
}

// https://codesandbox.io/s/k260nyxq9v?file=/index.js:1245-1511

// a little function to help with reordering the result
const reorder = (list: DataDto[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const SectionsDnd: FC<Props> = () => {
  const dispatch = useDispatch();
  const [isDragDisabled, setIsDragDisabled] = useState<boolean>(false);
  const langConfig = useSelector(internalizationState);
  const stateSections = useSelector(getAllSections);
  const [sections, setSections] = useState<DataDto[]>(stateSections);

  useEffect(() => {
    const isMiniLandingSection = stateSections.find(section => section.component === 'MiniLanding');
    
    if (isMiniLandingSection) {
      const filteredSections = [isMiniLandingSection, ...stateSections.filter(section => section.component !== 'MiniLanding')];
      setSections(filteredSections);
    }
    
  }, [stateSections])

  const handleParamUpdate = (value: string, sectionId: number, pid: number) => {
    dispatch(changeParam({ value, sectionId, pid }));
  };

  const handleParamCheckedUpdate = (value: boolean, sectionId: number, pid: number) => {
    dispatch(changeBooleanParam({ value, sectionId, pid }));
  };

  const handlerResetColor = (sectionId: number, pid: number) => {
    dispatch(resetColorToDefault({ sectionId, pid }));
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const items = reorder(sections, result.source.index, result.destination.index);
    dispatch(changeOrder(items));
  };

  const inputColorLabel = (param: ParamsDto) => {
    const ruLabel = param.label ? param.label : 'Основной цвет';
    const enLabel = param.label_en ? param.label_en : 'Main color'

    return langConfig.activeLangState === 'ru' ? ruLabel : enLabel;
  }

  const renderParam = (param: ParamsDto, sectionId: number) => {
    if (param.hasOwnProperty('visible') && param.visible === false) {
      return null;
    }

    switch (param.type) {
      case 'background':
      case 'introTitleColor':
      case 'introDescriptionColor':
      case 'introDateColor':
      case 'introTimeColor':
      case 'introStatusColor':
      case 'speakersSubstrateText':
      case 'speakersSubstrateButtonText':
        return (
          <Input
            aside="color"
            label={inputColorLabel(param)}
            icon="paint"
            hex={param.value}
            isRedifined={param.isRedifined ? param.isRedifined : false}
            handleChange={(e: string): void => handleParamUpdate(e, sectionId, param.id as number)}
            handlerReset={(): void => handlerResetColor(sectionId, param.id as number)}
          />
        );
      case 'mainBtnBackground':
      case 'mainBtnText':
      case 'secondBtnBackground':
      case 'secondBtnText':
      case 'introDateBackground':
      case 'introTimeBackground':
      case 'introStatusBackground':
      case 'programBackgound':
      case 'speakersSubstrateBackgound':
      case 'speakersSubstrateButtonBackgound':
      case 'introBlockBackground':
        return (
          <Input
            aside="color"
            label={langConfig.activeLangState === 'ru' ? param.label : param.label_en}
            icon="paint"
            hex={param.value}
            colorType="rgb"
            isRedifined={param.isRedifined ? param.isRedifined : false}
            handleChange={(e: string): void => handleParamUpdate(e, sectionId, param.id as number)}
            handlerReset={(): void => handlerResetColor(sectionId, param.id as number)}
          />
        );
      case 'number':
      case 'introTitleSize':
      case 'introDescriptionSize':
      case 'introDateSize':
      case 'introTimeSize':
      case 'introStatusSize':
        return (
          <Input
            type="number"
            label={langConfig.activeLangState === 'ru' ? param.label : param.label_en}
            value={param.value}
            handleChange={(e: string) => handleParamUpdate(e, sectionId, param.id as number)}
            handlerReset={(): void => handlerResetColor(sectionId, param.id as number)}
          />
        );
      // case 'image':
      //   return (
      //     <Input
      //       aside="image"
      //       label="Фоновое изображение"
      //       icon="upload"
      //       value={param.value}
      //       info="png, jpg"
      //       handleChange={(e: string) => handleParamUpdate(e, sectionId, param.id)}
      //     />
      //   );
      case 'title':
        return (
          <Input
            label={langConfig.activeLangState === 'ru' ? 'Текст заголовка' : 'Title text'}
            value={param.value}
            handleChange={(e: string) => handleParamUpdate(e, sectionId, param.id as number)}
          />
        );
      case 'checkbox':
        return (
          <Checkbox
            label={langConfig.activeLangState === 'ru' ? param.label : param.label_en}
            value={param.checked}
            handleChange={(e: boolean) => handleParamCheckedUpdate(e, sectionId, param.id as number)}
          />
        );
      case 'radio':
        return (
          <Radio
            value={param.value as string}
            params={param.params}
            handleChange={(value: string) => handleParamUpdate(value, sectionId, param.id as number)}
          />
        );

      case 'moreDetailsBtn':
        return (
          <MoreDetailsButton
            sectionId={sectionId}
            params={param as any}
          />
        );

      default:
        return null;
    }
  };

  const renderMenuVisibilityParam = (sectionId: number) => {
    const section = sections.find((section) => section.id === sectionId);

    if (section && section.hasOwnProperty('isNavigational')) {
      return (<Checkbox
        label={langConfig.activeLangState === 'ru' ? 'Отображать в меню' : 'Show on navigation'}
        value={section.isNavigational}
        handleChange={() => dispatch(toggleNavigationVisibility(sectionId))}
      />)
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            <ul className="setting-drop-list">
              {sections &&
                sections.map((section: any, index: any) => (
                  !section.isSettingTurnOff &&
                  <Draggable
                    isDragDisabled={isDragDisabled}
                    key={section.id}
                    draggableId={`drag-${section.id}`}
                    index={index}>
                    {(provided2, snapshot2) => (
                      <div
                        ref={provided2.innerRef}
                        {...provided2.draggableProps}
                        {...provided2.dragHandleProps}
                        className={cns('dragItem', snapshot2.isDragging && 'is-dragging')}
                        style={provided2.draggableProps.style}>
                        <Section id={section.id} title={{name: section.name, name_en: section.name_en}}>
                          {section.params && (
                            <ul
                              className="setting-options"
                              onMouseLeave={() => setIsDragDisabled(false)}
                              onMouseMove={() => setIsDragDisabled(true)}>
                              {section.params.map((param: any) => (
                                <li key={param.id}>{renderParam(param, section.id)}</li>
                              ))}
                              <li>{renderMenuVisibilityParam(section.id)}</li>
                            </ul>
                          )}
                        </Section>
                      </div>
                    )}
                  </Draggable>
                ))}
            </ul>
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default SectionsDnd;
